import { useCallback, useEffect, useRef } from "react";

/**
 * Phase 1: Web Audio API による合成音 (placeholder)。
 * Phase 2 で Howler + 録音素材に差し替え予定 (interface は維持)。
 */

type SfxName =
  | "bellShake"
  | "bellTap"
  | "woodTap"
  | "cardOpen"
  | "kirari"
  | "taikoDon"
  | "neraOpen"
  | "mikoVoice"
  | "tierUp"
  | "scrollOpen";

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

function envelope(
  node: GainNode,
  ctx: AudioContext,
  attack: number,
  release: number,
  peak = 0.4,
) {
  const now = ctx.currentTime;
  node.gain.cancelScheduledValues(now);
  node.gain.setValueAtTime(0, now);
  node.gain.linearRampToValueAtTime(peak, now + attack);
  node.gain.exponentialRampToValueAtTime(0.0001, now + attack + release);
}

function bell(ctx: AudioContext, freq: number, duration: number, peak = 0.35) {
  const osc1 = ctx.createOscillator();
  const osc2 = ctx.createOscillator();
  const gain = ctx.createGain();
  osc1.type = "sine";
  osc2.type = "sine";
  osc1.frequency.value = freq;
  osc2.frequency.value = freq * 2.76; // inharmonic partial = bell-like
  osc1.connect(gain);
  osc2.connect(gain);
  gain.connect(ctx.destination);
  envelope(gain, ctx, 0.005, duration, peak);
  const stopAt = ctx.currentTime + duration + 0.05;
  osc1.start();
  osc2.start();
  osc1.stop(stopAt);
  osc2.stop(stopAt);
}

function woodKnock(ctx: AudioContext, peak = 0.4) {
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.08, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 4);
  }
  const src = ctx.createBufferSource();
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 800;
  filter.Q.value = 4;
  const gain = ctx.createGain();
  gain.gain.value = peak;
  src.buffer = buffer;
  src.connect(filter).connect(gain).connect(ctx.destination);
  src.start();
}

function taiko(ctx: AudioContext) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(120, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.3);
  envelope(gain, ctx, 0.005, 0.5, 0.6);
  osc.connect(gain).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.6);
}

function shimmer(ctx: AudioContext) {
  // 金箔キラリン: 高周波の短いきらめき
  for (let i = 0; i < 6; i++) {
    setTimeout(
      () => bell(ctx, 1800 + Math.random() * 1200, 0.25, 0.18),
      i * 60,
    );
  }
}

function chord(
  ctx: AudioContext,
  notes: number[],
  duration = 0.6,
  peak = 0.18,
) {
  const gain = ctx.createGain();
  gain.connect(ctx.destination);
  envelope(gain, ctx, 0.04, duration, peak);
  const stopAt = ctx.currentTime + duration + 0.1;
  for (const f of notes) {
    const o = ctx.createOscillator();
    o.type = "triangle";
    o.frequency.value = f;
    // 軽いビブラート
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 5.5;
    lfoGain.gain.value = 2.4;
    lfo.connect(lfoGain).connect(o.frequency);
    o.connect(gain);
    o.start();
    lfo.start();
    o.stop(stopAt);
    lfo.stop(stopAt);
  }
}

function paperScroll(ctx: AudioContext) {
  // 巻物がほどける音: ホワイトノイズ → ローパス → ゆっくりエンベロープ
  const buf = ctx.createBuffer(1, ctx.sampleRate * 0.5, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  }
  const src = ctx.createBufferSource();
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 2200;
  const gain = ctx.createGain();
  gain.gain.value = 0.16;
  src.buffer = buf;
  src.connect(filter).connect(gain).connect(ctx.destination);
  src.start();
}

const playSfx = (name: SfxName) => {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") void c.resume();

  switch (name) {
    case "bellShake":
      bell(c, 1760, 0.6);
      setTimeout(() => bell(c, 1760, 0.5, 0.3), 90);
      setTimeout(() => bell(c, 2200, 0.4, 0.25), 200);
      break;
    case "bellTap":
      bell(c, 1980, 0.5);
      break;
    case "woodTap":
      woodKnock(c);
      break;
    case "cardOpen":
      // 和紙サラリ + キラリ
      woodKnock(c, 0.18);
      setTimeout(() => shimmer(c), 80);
      break;
    case "kirari":
      shimmer(c);
      break;
    case "taikoDon":
      taiko(c);
      setTimeout(() => bell(c, 2100, 0.6, 0.3), 200);
      break;
    case "neraOpen":
      // 和紙が破れる音 (placeholder)
      woodKnock(c, 0.25);
      setTimeout(() => woodKnock(c, 0.18), 50);
      break;
    case "mikoVoice": {
      // 巫女の小ボイス placeholder: 短い和音 (Phase 2 で「ふふ」録音に差し替え)
      const notes = [
        [392, 523, 659],
        [349, 440, 587],
        [330, 494, 659],
        [440, 587, 740],
      ];
      chord(c, notes[Math.floor(Math.random() * notes.length)], 0.55, 0.16);
      break;
    }
    case "tierUp":
      // ティアアンロック: 太鼓 + 神楽鈴
      taiko(c);
      setTimeout(() => shimmer(c), 200);
      setTimeout(() => bell(c, 1480, 0.7, 0.32), 400);
      break;
    case "scrollOpen":
      paperScroll(c);
      setTimeout(() => bell(c, 1320, 0.4, 0.22), 200);
      break;
  }
};

/**
 * Ambient drone: time-of-day に応じた穏やかな持続音。
 * Home 画面で start → stop で制御。base/partial で和的な開放音 (尺八/琴の placeholder)。
 */
type AmbientHandle = {
  stop: () => void;
};

let ambient: AmbientHandle | null = null;

function startAmbient(
  baseHz: number,
  partialHz: number,
  peak = 0.045,
): AmbientHandle {
  const c = getCtx();
  if (!c) return { stop: () => {} };
  if (c.state === "suspended") void c.resume();

  const now = c.currentTime;
  const master = c.createGain();
  master.gain.setValueAtTime(0, now);
  master.gain.linearRampToValueAtTime(peak, now + 2.5);
  master.connect(c.destination);

  const oscs: OscillatorNode[] = [];
  const lfos: OscillatorNode[] = [];

  const make = (
    f: number,
    type: OscillatorType,
    vibratoHz: number,
    vibratoDepth: number,
  ) => {
    const o = c.createOscillator();
    o.type = type;
    o.frequency.value = f;
    const lfo = c.createOscillator();
    const lfoGain = c.createGain();
    lfo.frequency.value = vibratoHz;
    lfoGain.gain.value = vibratoDepth;
    lfo.connect(lfoGain).connect(o.frequency);
    o.connect(master);
    o.start();
    lfo.start();
    oscs.push(o);
    lfos.push(lfo);
  };

  make(baseHz, "sine", 0.18, 1.4);
  make(partialHz, "triangle", 0.22, 1.8);
  make(baseHz / 2, "sine", 0.13, 0.9);

  return {
    stop: () => {
      const t = c.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
      const stopAt = t + 1.6;
      oscs.forEach((o) => o.stop(stopAt));
      lfos.forEach((o) => o.stop(stopAt));
    },
  };
}

export function setAmbient(baseHz: number | null, partialHz?: number) {
  if (ambient) {
    ambient.stop();
    ambient = null;
  }
  if (baseHz != null && partialHz != null) {
    ambient = startAmbient(baseHz, partialHz);
  }
}

/**
 * useSound: 演出音再生。AudioContext の resume はユーザージェスチャ要。
 * unlock() を任意のタップハンドラ内で1度呼んでおく。
 */
export function useSound() {
  const unlockedRef = useRef(false);

  const unlock = useCallback(() => {
    const c = getCtx();
    if (!c || unlockedRef.current) return;
    if (c.state === "suspended") void c.resume();
    unlockedRef.current = true;
  }, []);

  const play = useCallback((name: SfxName) => {
    playSfx(name);
  }, []);

  // ページ離脱時に context 状態整理 (任意)
  useEffect(() => {
    const onVisibility = () => {
      const c = ctx;
      if (!c) return;
      if (document.hidden && c.state === "running") void c.suspend();
      if (!document.hidden && c.state === "suspended" && unlockedRef.current)
        void c.resume();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  return { unlock, play };
}
